import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp } from 'lucide-react';
import SiteHeader from './SiteHeader';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const SORTS = [['revenue', 'Revenue'], ['budget', 'Budget'], ['roi', 'Return']];

// $450M, $2.92B, $5.5M. A loss keeps its sign in front of the dollar sign.
export const formatMoney = (value) => {
  if (value === null || value === undefined) return '—';
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  const scaled = (n, unit) => `${sign}$${Number(n.toFixed(2))}${unit}`;
  if (abs >= 1e9) return scaled(abs / 1e9, 'B');
  if (abs >= 1e6) return scaled(abs / 1e6, 'M');
  if (abs >= 1e3) return scaled(abs / 1e3, 'K');
  return `${sign}$${abs}`;
};

const DEAL_FILTERS = [
  [null, 'All deals'],
  ['MATERIAL_AGREEMENT', 'Agreements'],
  ['ACQUISITION_OR_DISPOSAL', 'Acquisitions'],
  ['DEBT_OBLIGATION', 'Debt'],
  ['AGREEMENT_TERMINATED', 'Terminated'],
];
export const DEAL_LABEL = {
  MATERIAL_AGREEMENT: 'Material agreement',
  ACQUISITION_OR_DISPOSAL: 'Acquisition or disposal',
  DEBT_OBLIGATION: 'Debt obligation',
  AGREEMENT_TERMINATED: 'Agreement terminated',
};

const formatGrowth = (pct) => (pct === null || pct === undefined ? '—' : `${pct > 0 ? '+' : ''}${pct}%`);

export default function Markets() {
  const [sort, setSort] = useState('revenue');
  const [titles, setTitles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [dealKind, setDealKind] = useState(null);
  const [deals, setDeals] = useState([]);

  // Same rule as companies: if the deal call fails the section stays hidden.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const kind = dealKind ? `&kind=${dealKind}` : '';
        const r = await fetch(`${API_BASE}/api/markets/deals?limit=20${kind}`);
        if (cancelled) return;
        if (r.ok) setDeals((await r.json()).deals || []);
      } catch { /* section stays hidden */ }
    })();
    return () => { cancelled = true; };
  }, [dealKind]);

  // Company filings change once a year, so they ignore the title sort. If they
  // fail the section stays hidden; the title table is the page.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/markets/companies?limit=20`);
        if (!cancelled && r.ok) setCompanies((await r.json()).companies || []);
      } catch { /* section stays hidden */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setFailed(false);
      try {
        const r = await fetch(`${API_BASE}/api/markets/titles?sort=${sort}&limit=50`);
        if (cancelled) return;
        if (r.ok) setTitles((await r.json()).titles || []);
        else { setTitles([]); setFailed(true); }
      } catch {
        if (!cancelled) { setTitles([]); setFailed(true); }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sort]);

  // The bar is the revenue, scaled to the biggest on screen.
  const maxRevenue = Math.max(1, ...titles.map((t) => t.revenue || 0));

  return (
    <div className="min-h-screen bg-gray-50">
      <SiteHeader />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2 mb-1">
          <TrendingUp className="w-6 h-6 text-videbits-600" /> Markets
        </h1>
        <p className="text-sm text-gray-500 mb-6">
          Film budgets, worldwide revenue and return on investment.
        </p>

        {companies.length > 0 && (
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Companies</h2>
            <div className="overflow-x-auto bg-white rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                    <th className="px-4 py-3">Company</th>
                    <th className="px-4 py-3 text-right">Revenue</th>
                    <th className="px-4 py-3 text-right">Growth</th>
                    <th className="px-4 py-3 text-right">Operating income</th>
                    <th className="px-4 py-3 text-right">Net income</th>
                    <th className="px-4 py-3 text-right">Year end</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((c) => (
                    <tr key={c.company_id} className="border-b border-gray-50 last:border-0">
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {c.name}{' '}
                        <a href={c.source_url} target="_blank" rel="noopener noreferrer"
                           className="text-xs font-normal text-videbits-700 hover:underline">
                          filing
                        </a>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{formatMoney(c.revenue)}</td>
                      <td className={`px-4 py-3 text-right tabular-nums ${c.revenue_growth < 0 ? 'text-red-600' : ''}`}>
                        {formatGrowth(c.revenue_growth)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{formatMoney(c.operating_income)}</td>
                      <td className={`px-4 py-3 text-right tabular-nums ${c.net_income < 0 ? 'text-red-600' : ''}`}>
                        {formatMoney(c.net_income)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-500">{c.period_end}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Company figures from SEC EDGAR 10-K filings, in US dollars, for the latest full year.
            </p>
          </section>
        )}

        {(deals.length > 0 || dealKind) && (
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Recent deals</h2>
            <div className="flex flex-wrap gap-2 mb-3">
              {DEAL_FILTERS.map(([key, label]) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => setDealKind(key)}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    dealKind === key
                      ? 'bg-videbits-600 text-white border-videbits-600'
                      : 'bg-white text-videbits-700 border-gray-200 hover:bg-videbits-50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {deals.length === 0 ? (
              <p className="text-gray-400 text-sm">No deals of this kind yet.</p>
            ) : (
              <ul className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-50">
                {deals.map((d) => (
                  <li key={d.accession + d.company_id} className="px-4 py-3 text-sm">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                      <span className="font-medium text-gray-900">{d.company}</span>
                      <span className="text-gray-500">{d.event_date}</span>
                    </div>
                    <div className="text-gray-700">{DEAL_LABEL[d.kind] || d.kind}</div>
                    <div className="text-xs text-gray-400">
                      {d.description}{' '}
                      <a href={d.source_url} target="_blank" rel="noopener noreferrer"
                         className="text-videbits-700 hover:underline">
                        filing
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-gray-400 mt-2">
              Deal filings (8-K) for the listed companies above. Deal amounts are not shown: the filing has them,
              and VideBits does not read them out.
            </p>
          </section>
        )}

        <h2 className="text-lg font-semibold text-gray-900 mb-2">Films</h2>
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 mb-6">
          {SORTS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setSort(key)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                sort === key ? 'bg-videbits-600 text-white' : 'bg-videbits-50 text-videbits-700 hover:bg-videbits-100'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <p className="text-gray-400 text-sm">Loading…</p>
        ) : failed ? (
          <p className="text-gray-500 text-sm">Markets data could not load. Try again soon.</p>
        ) : titles.length === 0 ? (
          <p className="text-gray-400 text-sm">No titles with known budget and revenue yet.</p>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                  <th className="px-4 py-3 w-8">#</th>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3 text-right">Budget</th>
                  <th className="px-4 py-3 text-right">Revenue</th>
                  <th className="px-4 py-3 text-right">Profit</th>
                  <th className="px-4 py-3 text-right">Return</th>
                </tr>
              </thead>
              <tbody>
                {titles.map((t, i) => (
                  <tr key={t.project_id} className="border-b border-gray-50 last:border-0">
                    <td className="px-4 py-3 text-gray-400">{i + 1}</td>
                    <td className="px-4 py-3 min-w-[12rem]">
                      <Link to={`/movie/${t.tmdb_id}`} className="font-medium text-gray-900 hover:text-videbits-700">
                        {t.title}
                      </Link>
                      <div className="mt-1 h-1.5 rounded-full bg-gray-100" aria-hidden="true">
                        <div
                          className="h-1.5 rounded-full bg-videbits-500"
                          style={{ width: `${Math.max(2, (100 * (t.revenue || 0)) / maxRevenue)}%` }}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatMoney(t.budget)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatMoney(t.revenue)}</td>
                    <td className={`px-4 py-3 text-right tabular-nums ${t.profit < 0 ? 'text-red-600' : 'text-green-700'}`}>
                      {formatMoney(t.profit)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{t.roi}×</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-xs text-gray-400 mt-4">
          Film data from TMDb, in US dollars. Figures are as reported, not live box office. Titles with an unknown
          budget or revenue are left out.
        </p>
      </div>
    </div>
  );
}
